import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  PDR_REQUIREMENT_ADJUSTMENT_EMAIL_PREVIEW_QUERY,
  type PdrRequirementAdjustmentEmailPreview,
  type PdrRequirementAdjustmentEmailPreviewQueryResponse,
  type PdrRequirementAdjustmentEmailPreviewQueryVariables
} from "../../queries";

export function usePdrRequirementAdjustmentEmailPreview() {
  const [preview, setPreview] = useState<PdrRequirementAdjustmentEmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    PdrRequirementAdjustmentEmailPreviewQueryResponse,
    PdrRequirementAdjustmentEmailPreviewQueryVariables
  >(PDR_REQUIREMENT_ADJUSTMENT_EMAIL_PREVIEW_QUERY, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (params: { pgaId: string; cycleCode: string }) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: {
          pgaId: params.pgaId,
          cycleCode: params.cycleCode
        }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.pdrRequirementAdjustmentEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
